<script setup lang="ts">
import { ref } from 'vue';
import { FwbTooltip } from 'flowbite-vue';
import { useSideNavCollapse } from '@base-shared/composables/use-sidenav-collapse';

const props = defineProps<{
  itemId: string;
  inverseAnimation?: boolean;
}>();

const emit = defineEmits<{
  (e: 'dropdownToggle', isOpen: boolean): void;
}>();

const { isSideNavCollapsed, collapseSideNav } = useSideNavCollapse();
const isOpen = ref(false);

const transitionAttrs = computed(() => ({
  enterFromClass: props.inverseAnimation ? 'translate-y-full' : '-translate-y-full',
  leaveToClass: props.inverseAnimation ? 'translate-y-full' : '-translate-y-full'
}));

function toggleDropdown(show: boolean) {
  isOpen.value = show;

  if (show) {
    collapseSideNav(false);
  }

  emit('dropdownToggle', show);
}

defineExpose({
  toggleDropdown
});
</script>

<template>
  <div class="overflow-hidden">
    <button
      v-if="!isSideNavCollapsed"
      type="button"
      class="flex items-center w-full p-2 text-base text-gray-900 transition-transform duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700 z-10"
      :data-component-element-id="`side-menu-dropdown-btn-${itemId}`"
      aria-controls="dropdown-content"
      @click="toggleDropdown(!isOpen)"
    >
      <slot name="icon" />

      <span class="flex-1 ml-3 text-left whitespace-nowrap">
        <slot name="trigger" />
      </span>

      <slot
        name="arrow-icon"
        :toggle-dropdown="toggleDropdown"
      >
        <IconSvg
          class="w-6! h-6! transition-transform duration-300"
          :class="{ 'rotate-180': (!inverseAnimation && isOpen) || (inverseAnimation && !isOpen) }"
          name="flowbite:chevron-down-outline"
        />
      </slot>
    </button>

    <FwbTooltip
      v-else
      placement="right"
    >
      <template #trigger>
        <button
          type="button"
          class="flex items-center p-2 text-base text-gray-900 transition-transform duration-75 rounded-lg group hover:bg-gray-100 dark:text-white dark:hover:bg-gray-700 z-10 mx-[10px]"
          :data-component-element-id="`side-menu-dropdown-btn-${itemId}`"
          aria-controls="dropdown-content"
          @click="toggleDropdown(true)"
        >
          <slot name="icon" />
        </button>
      </template>
      <template #content>
        <slot name="trigger" />
      </template>
    </FwbTooltip>

    <div class="pt-2 space-y-2 z-0 overflow-hidden">
      <transition
        :duration="150"
        v-bind="transitionAttrs"
        enter-to-class="translate-y-0"
        enter-active-class="transition duration-400 ease-out"
        leave-active-class="transition duration-400 ease-in"
        leave-from-class="translate-y-0"
      >
        <div v-if="isOpen && !isSideNavCollapsed">
          <slot name="default" />
        </div>
      </transition>
    </div>
  </div>
</template>
